import {
  Menu,
  Text,
  UnstyledButton,
  Loader,
  Tooltip,
  Paper,
  Box,
  Divider,
} from "@mantine/core"
import {
  IconCalendarQuestion,
  IconCancel,
  IconCheck,
  IconCheckbox,
  IconNote,
  IconQuestionMark,
} from "@tabler/icons-react"
import {
  CastDto,
  CellData,
  PersonStatusDto,
  WithPermissions,
} from "../../../../../../@client/Theasoft_ApiServer_Orchestra"
import { CastCellDropdown } from "./cast-dropdown"
import { colorToHex } from "../../../../../../utils/color-to-rgba"
import { match, P } from "ts-pattern"
import { useTranslation } from "react-i18next"
import { clsx } from "clsx"
import css from "./cells.module.css"
import { SimpleShiftType } from "../../../../../../@client/Theasoft_ApiServer_ShiftTypes"
import { CSSProperties, useMemo } from "react"
import { filterNullOrUndefined } from "../../../../../../utils/filter-null"
import { FontFormat } from "@/@client/Theasoft_Framework"
import { useBatchedCastStatus } from "@/-dataloaders/cast-status"
import { PersonName } from "@components/PersonName"

type CellProps = {
  availableAbsenceShiftTypes: SimpleShiftType[]
  personStatuses: WithPermissions<PersonStatusDto>[]
  quickAdd: boolean
  quickDelete: boolean
  canAdd: boolean
  canModify: boolean
  isFirstInGroup: boolean
  isLoading: boolean
  showCastStatus: boolean
  selected: boolean
  expectedWorkareaCasts: number
  noTarget: boolean
  contractId: number
  workareaId?: number
  personId: number
  cell: CellData | null | undefined
  cast: CastDto[] | null
  activityId: number | null
  productionAllowed: boolean
  hasDepartmentWritePermission: boolean
  onQuickDeleteClicked: ({ castId }: { castId: number }) => void
  onCastClicked: (v: {
    cast: CastDto
    contractId: number
    activityId: number
  }) => void
  onAddAbsenceClicked: (v: {
    personId: number
    contractId: number
    shiftTypeId: number
  }) => void
  onEditAbsenceClicked: (v: PersonStatusDto) => void
  onQuickCreateCastClicked: (v: {
    personId: number
    contractId: number
    activityId: number
  }) => void
  onCreateCastClicked: (v: {
    personId: number
    contractId: number
    activityId: number
  }) => void
}

const getFontStyle = (v?: FontFormat) =>
  v?.font.italic === "True" ? "italic" : undefined

function renderCellText(cell: CellData | null | undefined) {
  const color = cell
    ? colorToHex(cell.foregroundColor, { mapBlackAndWhiteToUndefined: true })
    : undefined
  return cell === null || cell === undefined
    ? null
    : cell.textFragments?.map((v, i) => (
        <span key={i}>
          {i > 0 ? ", " : null}
          {v.format?.font?.strikeout == "True" ? (
            <IconCancel
              size={24}
              style={{
                position: "absolute",
                top: "60%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                opacity: 0.5,
              }}
            />
          ) : null}
          <Text
            c={color}
            span
            size="xs"
            fw={
              cell.hasAbsenceInfo
                ? undefined
                : v.format?.font.bold
                  ? 700
                  : undefined
            }
            fs={getFontStyle(v.format)}
          >
            {v.text + ""}
          </Text>
        </span>
      ))
}

type QuickAddAction = {
  Case: "quick-add"
  Data: Parameters<CellProps["onQuickCreateCastClicked"]>[0]
  Cursor: CSSProperties["cursor"]
}

type OpenCreateCastDetails = {
  Case: "open-create"
  Data: Parameters<CellProps["onCreateCastClicked"]>[0]
  Cursor: CSSProperties["cursor"]
}

type OpenContextMenu = {
  Case: "open-context-menu"
  Cursor: CSSProperties["cursor"]
}

type NoOp = {
  Case: "no-op"
  Cursor: CSSProperties["cursor"]
}

type ActionOnCellClick =
  | QuickAddAction
  | NoOp
  | OpenCreateCastDetails
  | OpenContextMenu

export function CastCell({
  quickAdd,
  quickDelete,
  canAdd,
  canModify,
  cell,
  showCastStatus,
  activityId,
  onCastClicked,
  contractId,
  personId,
  onCreateCastClicked,
  onQuickCreateCastClicked: onQuickCreateClicked,
  onAddAbsenceClicked,
  onQuickDeleteClicked,
  noTarget,
  selected,
  isLoading,
  availableAbsenceShiftTypes,
  personStatuses,
  productionAllowed,
  hasDepartmentWritePermission,
  ...props
}: CellProps) {
  const cast: CastDto[] = props.cast ?? []

  const backColors = personStatuses
    .map((v) => v.value.statusTypPrintColors?.backColor)
    .filter(filterNullOrUndefined)
  const bgColor =
    backColors && backColors.length > 0
      ? colorToHex(backColors[0])
      : cell
        ? colorToHex(cell.backgroundColor, {
            mapBlackAndWhiteToUndefined: true,
          })
        : undefined

  const { t } = useTranslation("app_server")
  const [menuIsOpen, setMenuIsOpen] = React.useState(false)

  const classNames = clsx(
    css["cast-cell"],
    !canAdd && css["disabled-cell"],
    noTarget && css["disabled-cell"],
    activityId === null && css["disabled-cell"]
  )

  const hasCasts = cast.length > 0
  const canCast = activityId !== null && !noTarget && canAdd && !hasCasts
  const action: ActionOnCellClick = useMemo(() => {
    const absencesAvailable = availableAbsenceShiftTypes.length > 0
    const hasPersonStatuses = personStatuses.length > 0
    return canCast && quickAdd && !hasCasts
      ? {
          Case: "quick-add",
          Data: { personId, activityId, contractId },
          Cursor: "copy",
        }
      : canCast && !hasCasts && !absencesAvailable
        ? {
            Case: "open-create",
            Data: { personId, activityId, contractId },
            Cursor: "pointer",
          }
        : canCast ||
            absencesAvailable ||
            (canModify && (hasPersonStatuses || hasCasts))
          ? {
              Case: "open-context-menu",
              Cursor: "pointer",
            }
          : { Case: "no-op", Cursor: "unset" }
  }, [
    canAdd,
    activityId,
    quickAdd,
    noTarget,
    cast,
    availableAbsenceShiftTypes,
    personStatuses,
  ])

  const tooltipContent = (
    <>
      <PersonName personId={personId} />
      {cast.map((v, index, arr) => (
        <React.Fragment key={v.castId}>
          {arr.length > 1 ? `${v.roleName}` : ""}
          {v.note ? <Text fz={"sm"}>{`${t("Note")}: ${v.note}`}</Text> : ""}
          {v.castStatusId ? (
            <Text fz={"sm"}>
              {t("CastStatus")}: <CastStatus cast={v} />
            </Text>
          ) : (
            ""
          )}
          {arr.length > 1 && index < arr.length - 1 ? (
            <Divider size={"sm"} />
          ) : null}
        </React.Fragment>
      ))}
    </>
  )

  return (
    <Box
      className={classNames}
      style={{
        border:
          selected || menuIsOpen
            ? "1px solid var(--mantine-primary-color-8)"
            : "1px solid transparent",
        backgroundColor: productionAllowed ? bgColor : "gray",
      }}
    >
      {isLoading ? (
        <Loader color="blue" type="dots" size="xs" />
      ) : false ? (
        <UnstyledButton
          w="100%"
          h="100%"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "hidden",
            textAlign: "center",
          }}
        >
          {null}
        </UnstyledButton>
      ) : cast === null ? null : cast.length >= 0 ? (
        <Menu
          shadow="md"
          opened={menuIsOpen}
          onClose={() => setMenuIsOpen(false)}
        >
          <Menu.Target>
            <Tooltip
              events={{ hover: true, touch: true, focus: false }}
              disabled={action.Case !== "no-op" || !hasCasts}
              label={tooltipContent}
            >
              <UnstyledButton
                w="100%"
                h="100%"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "hidden",
                  textAlign: "center",
                  cursor: action.Cursor,
                }}
                onClick={() => {
                  match(action)
                    .with(
                      { Case: "quick-add", Data: P.select() },
                      onQuickCreateClicked
                    )
                    .with(
                      { Case: "open-create", Data: P.select() },
                      onCreateCastClicked
                    )
                    .with({ Case: "no-op" }, () => {})
                    .with({ Case: "open-context-menu" }, () =>
                      setMenuIsOpen(true)
                    )
                    .exhaustive()
                }}
              >
                {renderCellText(cell) || " "}
                <Text span size="xs">
                  {/* TODO: color not applied */}
                  {personStatuses.map((v) => v.value.statusDisplayName)}
                </Text>
                {cast.some((v) => v.note && v.note !== "") ? (
                  <IconNote
                    size={10}
                    color="orange"
                    style={{
                      position: "absolute",
                      right: 2,
                      top: 14,
                    }}
                  />
                ) : null}
                <>
                  {showCastStatus
                    ? cast.map((v, index) => (
                        <CastStatusTooltip
                          index={index}
                          key={v.castId}
                          cast={v}
                        />
                      ))
                    : null}
                </>
                {/* backend does not yield text fragments if cast is canceled and creditable value is zero */}
                {cell?.textFragments?.length === 0 &&
                cast.some((v) => Boolean(v.isCanceled)) ? (
                  <Tooltip label={t("Canceled")}>
                    <IconCancel
                      size={24}
                      style={{
                        position: "absolute",
                        top: "60%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        opacity: 0.5,
                      }}
                    />
                  </Tooltip>
                ) : null}
                {cast.some((v) => Boolean(v.isNew)) ? (
                  <Paper
                    bg="teal"
                    p={1}
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      borderTopRightRadius: 0,
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                  >
                    <Text
                      c="white"
                      style={{
                        lineHeight: 1,
                        padding: 1,
                        fontSize: 8,
                      }}
                    >
                      {t("New")}
                    </Text>
                  </Paper>
                ) : null}
              </UnstyledButton>
            </Tooltip>
          </Menu.Target>
          <CastCellDropdown
            hasDepartmentWritePermission={hasDepartmentWritePermission}
            onQuickDelete={(v) =>
              activityId &&
              contractId &&
              onQuickDeleteClicked({
                castId: v.castId,
              })
            }
            allowDelete={quickDelete && canModify}
            personStatuses={personStatuses}
            availableAbsenceShiftTypes={availableAbsenceShiftTypes}
            allowAdd={canCast}
            allowAddAbsence={canModify}
            onAddAbsenceClicked={(shiftTypeId: number) =>
              onAddAbsenceClicked({ personId, contractId, shiftTypeId })
            }
            onEditAbsenceClicked={props.onEditAbsenceClicked}
            onAddClicked={() => {
              activityId &&
                onCreateCastClicked({ contractId, personId, activityId })
            }}
            onCastSelected={(cast) => {
              activityId && onCastClicked({ cast, contractId, activityId })
            }}
            casts={cast}
          />
        </Menu>
      ) : null}
    </Box>
  )
}

function CastStatusTooltip({ cast, index }: { cast: CastDto; index: number }) {
  const { t } = useTranslation("app_server")
  const { data: castStatus } = useBatchedCastStatus(cast.castStatusId)

  const Icon = match(cast.castStatusType)
    .with("OK", () => IconCheck)
    .with("Plan", () => IconCheckbox)
    .with("Request", () => IconQuestionMark)
    .with("Suggestion", () => IconCalendarQuestion)
    .otherwise(() => IconQuestionMark)
  return (
    <Tooltip label={`${t("CastStatus")} ${castStatus?.name}`}>
      <Icon
        size={10}
        style={{
          position: "absolute",
          right: 2,
          top: 2 + 8 * index,
        }}
      />
    </Tooltip>
  )
}

function CastStatus({ cast }: { cast: CastDto }) {
  const { data: castStatus } = useBatchedCastStatus(cast.castStatusId)
  return castStatus?.name
}
